import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, CheckCircle2, ChevronLeft } from "lucide-react";
import { useKuizyTheme } from "@/styles/kuizyTheme";
import ThemeMenu from "@/widgets/ThemeMenu";
import { resetLearnerPassword } from "@/api/auth";

const fade = { initial:{opacity:0,y:12}, animate:{opacity:1,y:0}, transition:{duration:.25} };

export default function LearnerResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme, t } = useKuizyTheme();
  const [pw, setPw] = useState("");
  const [cpw, setCpw] = useState("");
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const linkCls = useMemo(
    () => (theme === "secondary" ? "text-secondary hover:opacity-80" : "text-primary hover:opacity-80"),
    [theme]
  );

  const inputCls = `w-full h-11 rounded-xl pl-9 pr-11 outline-none ${t.input}`;
  const iconLeftCls = "size-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400";
  const iconRightBtnCls = "absolute right-3 top-1/2 -translate-y-1/2";
  const iconRightCls = "size-4 text-gray-400";

  const onSubmit = async e => {
    e.preventDefault();
    setErr("");
    if (!pw || pw.length < 6) { setErr("รหัสผ่านอย่างน้อย 6 ตัวอักษร"); return; }
    if (cpw && pw !== cpw) { setErr("รหัสผ่านไม่ตรงกัน"); return; }
    setLoading(true);
    try {
      await resetLearnerPassword(token, pw);
      setDone(true);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.response?.data?.message || "ไม่สามารถตั้งรหัสผ่านใหม่ได้";
      setErr(String(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { document.title = "ตั้งรหัสผ่านใหม่ • Kuizy"; }, []);

  return (
    <div className={`min-h-screen ${t.bg}`}>
      <ThemeMenu />
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <img src="/images/app-logo.png" alt="Kuizy" className="h-20 mx-auto opacity-95" />
          <motion.div {...fade} className={`mt-5 p-6 rounded-2xl ${t.card}`}>
            {!done ? (
              <>
                <div className={`text-xl font-semibold ${t.text}`}>ตั้งรหัสผ่านใหม่</div>
                <form onSubmit={onSubmit} className="mt-5 space-y-3">
                  <div className="relative">
                    <Lock className={iconLeftCls} />
                    <input
                      type={show ? "text" : "password"}
                      className={inputCls}
                      placeholder="รหัสผ่านใหม่"
                      value={pw}
                      onChange={e=>setPw(e.target.value)}
                    />
                    <button type="button" className={iconRightBtnCls} onClick={()=>setShow(s=>!s)}>
                      {show ? <EyeOff className={iconRightCls} /> : <Eye className={iconRightCls} />}
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className={iconLeftCls} />
                    <input
                      type={show2 ? "text" : "password"}
                      className={inputCls}
                      placeholder="ยืนยันรหัสผ่าน (ไม่บังคับ)"
                      value={cpw}
                      onChange={e=>setCpw(e.target.value)}
                    />
                    <button type="button" className={iconRightBtnCls} onClick={()=>setShow2(s=>!s)}>
                      {show2 ? <EyeOff className={iconRightCls} /> : <Eye className={iconRightCls} />}
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !pw}
                    className={`w-full h-11 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 ${t.btn}`}
                  >
                    <CheckCircle2 className="size-4" />
                    ยืนยัน
                  </button>
                  {err ? <div className="text-danger text-sm mt-2">{err}</div> : null}
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className={`${t.subtext} flex items-center gap-1`}><ChevronLeft className="size-4" /> </span>
                    <Link to="/learn/login" className={linkCls}>กลับสู่หน้าเข้าสู่ระบบ</Link>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className={`text-xl font-semibold ${t.text}`}>ตั้งรหัสผ่านเรียบร้อย</div>
                <Link to="/learn/login" className={`inline-block mt-5 h-10 px-5 rounded-xl ${t.btn}`}>เข้าสู่ระบบ</Link>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
